import { PropApplicability } from 'BMapsSrc/model/ScopedPropertyGroup';
import { updateLocalJobState } from 'BMapsSrc/redux/jobs/access';
import { JobDefinition, JobResultDestinations, JobTypes } from '../JobDefinition';

/**
 * Molexar pocket-conditioned generation job.
 *
 * Unlike DrugFlow (SDF/3D output), Molexar emits a plain `.smi` file -- one
 * canonical SMILES per line, no header, no 3D coordinates -- so this mirrors a
 * SMILES-producing job (Reinvent) for import: each line becomes a compound
 * carrying its SMILES, and 3D structure is generated on import.
 *
 * @augments JobDefinition
 */
export class MolexarJob extends JobDefinition {
    static jobTypeId = JobTypes.MOLEXAR;
    static resultDestination = JobResultDestinations.COMPOUND;
    static jobTypeName = 'Molexar';
    static jobName = 'Molexar Job';

    /** Results rows keyed by their generated id. */
    static idField = 'id';

    /**
     * Results-table columns for generated compounds.
     * @override
     * @returns {Object<string, import('../JobDefinition').DataInfoEntry>}
     */
    static getDataInfo() {
        return {
            SMILES: {
                label: 'Structure', structureValue: true, defaultVisible: true, propApplicability: PropApplicability.PERCOMPOUND,
            },
            name: {
                label: 'Name', type: 'string', defaultVisible: true, propApplicability: PropApplicability.PERCOMPOUND,
            },
        };
    }

    /**
     * Parses the Molexar `.smi` output into generated structures and stores them
     * on local job state for the results table / import.
     * @override
     * @param {object} job
     * @param {object|string} results
     * @param {import('../JobDefinition').IntegrateResultsOptions} [_options]
     * @returns {Promise<import('../JobDefinition').IntegrateResultsReturn>}
     */
    async integrateResults(job, results, _options) {
        let resultsPacket = results;
        let structures = [];
        let error;
        try {
            if (!resultsPacket) return { error: 'Empty results' };
            if (typeof resultsPacket === 'string') resultsPacket = JSON.parse(resultsPacket);
            const smi = resultsPacket.jobResults?.smi;
            if (!smi) return { error: 'Empty results' };
            structures = parseMolexarSmi(smi).map((smiles, i) => ({
                id: `${job.jobid}_${i}`,
                name: `molexar_${i + 1}`,
                SMILES: smiles || null,
            }));
            updateLocalJobState(job.jobid, { structures });
        } catch (ex) {
            error = `Error loading ${this.jobTypeId} results`;
            console.error(error, ex);
        }
        return {
            error,
            results: { structures, jobResults: resultsPacket.jobResults },
        };
    }
}

/**
 * Parse Molexar's `.smi` output: one canonical SMILES per line, no header. A
 * `.smi` line may optionally carry a whitespace-separated name after the SMILES,
 * so keep only the first token per non-empty line.
 * @param {string} smi
 * @returns {string[]} canonical SMILES strings
 */
function parseMolexarSmi(smi) {
    return smi
        .split('\n')
        .map((l) => l.trim())
        .filter((l) => l !== '')
        .map((l) => l.split(/\s+/)[0]);
}
